import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// Public, unauthenticated page — an Ambassador is a member, not COMS staff,
// so there is no Auth0 login here. Reached via a personal link/QR
// (playpicklr.com/ambassador-log/{code}); the code itself is the credential,
// validated server-side on every call. See docs/COMS_Ambassador_Program_Build_Spec.md.
// This replaces "The Picklr Ambassador Log 2026" Google Form — same category
// labels, so Ambassadors aren't relearning anything, plus a running point
// total and the growth categories that form never captured.

const API_BASE = process.env.REACT_APP_API_BASE_URL;

async function apiCall(path, options = {}) {
  const res = await fetch(`${API_BASE}/api${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  let body = null;
  try { body = await res.json(); } catch { /* empty body */ }
  if (!res.ok) throw new Error(body?.error || `Request failed (${res.status})`);
  return body;
}

const BASELINE_ORDER = [
  'open_play_or_program', 'league', 'tournament', 'clinic_conducted',
  'own_program_conducted', 'social_media_post',
];
const GROWTH_ORDER = [
  'new_lead_referred', 'pb101_registration', 'trial_start', 'membership_conversion',
  'guest_event_social', 'coached_pb101', 'helped_run_league', 'mentored_new_member',
  'member_feedback_reported', 'helped_app_walkthrough', 'testimonial', 'outside_tournament_rep',
];

export default function AmbassadorLogPage() {
  const { code } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ready | not_found | error
  const [ambassador, setAmbassador] = useState(null);
  const [actionTypes, setActionTypes] = useState({});
  const [selectedType, setSelectedType] = useState('');
  const [detail, setDetail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null); // { ok: bool, text: string }

  async function load() {
    try {
      const data = await apiCall(`/ambassador/log/${encodeURIComponent(code)}`);
      setAmbassador(data.ambassador);
      setActionTypes(data.actionTypes || {});
      setStatus('ready');
    } catch (err) {
      setStatus(err.message === 'invalid_or_inactive_code' ? 'not_found' : 'error');
    }
  }

  useEffect(() => { load(); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selectedType) return;
    setSubmitting(true);
    setSubmitMsg(null);
    try {
      await apiCall(`/ambassador/log/${encodeURIComponent(code)}`, {
        method: 'POST',
        body: JSON.stringify({ actionType: selectedType, detail }),
      });
      setSubmitMsg({ ok: true, text: 'Logged — pending your club manager\'s approval.' });
      setSelectedType('');
      setDetail('');
      load(); // refresh pending count
    } catch (err) {
      const friendly = err.message === 'cap_reached'
        ? "You've already logged the max for this category this period."
        : err.message;
      setSubmitMsg({ ok: false, text: friendly });
    } finally {
      setSubmitting(false);
    }
  }

  const wrap = {
    minHeight: '100vh', background: '#0a0a0a', color: '#e5e5e5',
    fontFamily: 'DM Sans, sans-serif', padding: '24px 16px', maxWidth: 480, margin: '0 auto',
  };
  const card = {
    background: '#111', border: '1px solid #222', borderRadius: 10, padding: 16, marginBottom: 14,
  };
  const label = { fontSize: 12, color: '#888', marginBottom: 4 };
  const select = {
    width: '100%', background: '#0a0a0a', color: '#e5e5e5', border: '1px solid #333',
    borderRadius: 8, padding: '10px 12px', fontSize: 15, marginBottom: 12,
  };
  const input = { ...select };
  const button = {
    width: '100%', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8,
    padding: '12px 16px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
  };

  if (status === 'loading') return <div style={wrap}>Loading…</div>;
  if (status === 'not_found') {
    return <div style={wrap}><div style={card}>This link isn't recognized. Check with your club manager for your current Ambassador log link.</div></div>;
  }
  if (status === 'error') {
    return <div style={wrap}><div style={card}>Something went wrong loading your log. Try again in a moment.</div></div>;
  }

  return (
    <div style={wrap}>
      <h1 style={{ fontSize: 20, marginBottom: 2 }}>Ambassador Log</h1>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 18 }}>{ambassador.ambassador_name} · {ambassador.location_id}</div>

      <div style={card}>
        <div style={label}>This quarter ({ambassador.quarter})</div>
        <div style={{ fontSize: 28, fontWeight: 800 }}>{ambassador.approvedPoints} pts</div>
        {ambassador.pendingCount > 0 && (
          <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 4 }}>
            + {ambassador.pendingCount} pending your manager's approval
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} style={card}>
        <div style={label}>What did you do?</div>
        <select style={select} value={selectedType} onChange={e => setSelectedType(e.target.value)} required>
          <option value="">Select one…</option>
          <optgroup label="Regular engagement">
            {BASELINE_ORDER.filter(k => actionTypes[k]).map(k => (
              <option key={k} value={k}>{actionTypes[k].label}</option>
            ))}
          </optgroup>
          <optgroup label="Growing the club">
            {GROWTH_ORDER.filter(k => actionTypes[k]).map(k => (
              <option key={k} value={k}>{actionTypes[k].label}</option>
            ))}
          </optgroup>
        </select>

        <div style={label}>Name, short description, or link (optional)</div>
        <input
          style={input} type="text" value={detail} maxLength={280}
          placeholder="e.g. who you referred, a link to your post"
          onChange={e => setDetail(e.target.value)}
        />

        <button style={{ ...button, opacity: submitting ? 0.6 : 1 }} disabled={submitting} type="submit">
          {submitting ? 'Logging…' : 'Log it'}
        </button>

        {submitMsg && (
          <div style={{ marginTop: 10, fontSize: 13, color: submitMsg.ok ? '#22c55e' : '#ef4444' }}>
            {submitMsg.text}
          </div>
        )}
      </form>
    </div>
  );
}
